import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { RESOURCE_GROUPS, RESOURCE_DISCLAIMER } from "@/data/content";
import PageHero from "@/components/site/PageHero";
import ReadinessAssessment from "@/components/site/ReadinessAssessment";
import CTABand from "@/components/site/CTABand";

export default function Resources() {
  useDocumentTitle("Resources | An open library for trustworthy technology — Meta61");
  const location = useLocation();
  const assessmentRef = useRef(null);

  useEffect(() => {
    if (location.hash === "#assessment" && assessmentRef.current) {
      setTimeout(() => assessmentRef.current.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [location.hash]);

  return (
    <div>
      <PageHero
        title="An open library for"
        accent="trustworthy technology."
        subhead="A curated, freely available collection of the standards, principles, and frameworks Meta61 works with — so you can see how we think, and use the same foundations we do."
      />

      {/* Assessment */}
      <section id="assessment" ref={assessmentRef} className="m61-section scroll-mt-24">
        <div className="m61-container">
          <p className="m61-eyebrow mb-6">Convergence Readiness Assessment</p>
          <ReadinessAssessment />
        </div>
      </section>

      {/* Curated resources */}
      <section className="m61-section border-t border-m61-line bg-m61-surface/40">
        <div className="m61-container">
          <p className="m61-eyebrow mb-10">Curated open resources</p>
          <div className="space-y-12">
            {RESOURCE_GROUPS.map((g) => (
              <div key={g.heading}>
                <h3 className="m61-h3 mb-5 text-m61-cyan">{g.heading}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {g.items.map((item) => (
                    <a key={item.name} href={item.url} target="_blank" rel="noopener noreferrer" className="m61-card p-6 flex flex-col group">
                      <h4 className="text-m61-text font-display mb-2 group-hover:text-m61-cyan transition-colors flex items-start justify-between gap-2">
                        {item.name} <ArrowUpRight className="w-4 h-4 text-m61-muted shrink-0 mt-1" />
                      </h4>
                      <p className="text-m61-muted text-sm">{item.desc}</p>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-sm text-m61-muted max-w-3xl">{RESOURCE_DISCLAIMER}</p>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
